import React from "react";
import { buildTaskElementLookup, resolveRouteToSteps } from "../../utils/bpmnAnimationHelpers";
import "../../styles/bpmn-token-animation.css";

const STEP_MS_BY_SPEED = { slow: 1400, normal: 900, fast: 450 };

const fmtInt = (n) => Number(n || 0).toLocaleString();

/**
 * BPMN animation panel with two replay modes over the SAME backend-computed data:
 *
 * 1) "Single route" — walks ONE enumerated execution route (paths[].route) as a
 *    highlighted token, exactly as before.
 *
 * 2) "All simulated cases" (Bizagi-style) — replays how the Monte-Carlo
 *    simulation's cases (FBPM Ch.7 §7.4) distributed across the routes.
 *    The backend's `simulation.path_case_counts` records how many of the
 *    10,000 seeded iterations took each route; the replay walks the routes as
 *    "waves" (largest first) and every element the wave passes gets a counter
 *    badge accumulating the number of simulated cases that arrived there.
 *    Final badge ÷ iterations reproduces each task's activation probability —
 *    the same figure the per-activity table reports, which is the built-in
 *    consistency check. Counts are backend numbers; this panel only sums them
 *    over the routes that visit each element, it computes nothing new.
 *    The mode is hidden (not broken) when the backend response predates
 *    `path_case_counts`.
 *
 * Route data covers tasks and sub-process steps — gateways/events carry no
 * counters because the enumerated routes are task-level.
 *
 * Props:
 * - viewer: live bpmn-js viewer (from BpmnInteractiveViewer's onViewerReady)
 * - paths: redesign_flow.{as_is|to_be}.paths[]
 * - tasks: redesign_flow.{as_is|to_be}.tasks[]
 * - simulation: redesign_flow.{as_is|to_be}.simulation (for path_case_counts)
 */
export default function BpmnPathAnimator({ viewer, paths, tasks, simulation }) {
  const [mode, setMode] = React.useState("route"); // "route" | "cases"
  const [pathIndex, setPathIndex] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(-1);
  const [speed, setSpeed] = React.useState("normal");
  // Cases-mode display state (the engine itself runs on refs to stay
  // interval-safe; this is only what the status line renders).
  const [caseStatus, setCaseStatus] = React.useState(null); // {waveNo, waveCount, routeNo, cases, sharePct, stepNo, stepCount, label, done}
  const timerRef = React.useRef(null);
  const markedRef = React.useRef([]);
  const badgesRef = React.useRef(new Map()); // elementId -> { overlayId, node, count }
  const caseCursorRef = React.useRef({ wave: 0, step: -1 });

  const rows = React.useMemo(() => (Array.isArray(paths) ? paths : []), [paths]);
  const lookup = React.useMemo(() => buildTaskElementLookup(tasks), [tasks]);
  const nameById = React.useMemo(() => {
    const m = new Map();
    for (const [name, id] of lookup) m.set(id, name);
    return m;
  }, [lookup]);
  const activePath = rows[Math.min(pathIndex, rows.length - 1)];
  const resolved = React.useMemo(
    () => (activePath ? resolveRouteToSteps(activePath.route, lookup) : { steps: [], unresolved: [] }),
    [activePath, lookup],
  );

  // The simulation's case-per-route split, largest wave first, each with its
  // route resolved to diagram element ids. Null when the backend response
  // doesn't carry path_case_counts yet (older deploy) — the mode then hides.
  const totalCases = simulation?.iterations || 0;
  const waves = React.useMemo(() => {
    const pcc = simulation?.path_case_counts;
    if (!Array.isArray(pcc) || !pcc.length || !rows.length) return null;
    const out = pcc
      .filter((c) => c && c.cases > 0 && rows[c.path_index])
      .map((c) => ({
        routeNo: c.path_index + 1, // matches the Path View table's numbering
        cases: c.cases,
        sharePct: c.share_pct,
        resolved: resolveRouteToSteps(rows[c.path_index].route, lookup),
      }))
      .sort((a, b) => b.cases - a.cases);
    return out.length ? out : null;
  }, [simulation, rows, lookup]);

  const clearMarkers = React.useCallback(() => {
    if (!viewer) return;
    try {
      const canvas = viewer.get("canvas");
      for (const id of markedRef.current) {
        canvas.removeMarker(id, "bpmn-token-active");
        canvas.removeMarker(id, "bpmn-token-visited");
      }
    } catch {
      // Diagram may already be torn down (view switched mid-animation).
    }
    markedRef.current = [];
  }, [viewer]);

  const clearBadges = React.useCallback(() => {
    if (viewer) {
      try {
        const overlays = viewer.get("overlays");
        for (const { overlayId } of badgesRef.current.values()) overlays.remove(overlayId);
      } catch {
        // Viewer torn down — overlays died with it.
      }
    }
    badgesRef.current = new Map();
  }, [viewer]);

  const stop = React.useCallback(() => {
    setPlaying(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = React.useCallback(() => {
    stop();
    clearMarkers();
    clearBadges();
    setStepIndex(-1);
    caseCursorRef.current = { wave: 0, step: -1 };
    setCaseStatus(null);
  }, [stop, clearMarkers, clearBadges]);

  // A different route, mode, diagram, or the diagram unmounting all invalidate
  // whatever was mid-flight.
  React.useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathIndex, mode, viewer, activePath]);

  React.useEffect(
    () => () => {
      clearMarkers();
      clearBadges();
      if (timerRef.current) clearInterval(timerRef.current);
    },
    [clearMarkers, clearBadges],
  );

  const stepIds = (step) => (typeof step === "string" ? [step] : step.concurrent);

  // ── Mode 1: single-route walkthrough (unchanged behavior) ────────────────
  const advanceRoute = React.useCallback(() => {
    if (!viewer) return;
    let canvas;
    try {
      canvas = viewer.get("canvas");
    } catch {
      stop();
      return;
    }
    setStepIndex((prev) => {
      const next = prev + 1;
      if (next >= resolved.steps.length) {
        stop();
        return prev;
      }
      const prevStep = resolved.steps[prev];
      if (prevStep != null) {
        for (const id of stepIds(prevStep)) {
          try {
            canvas.removeMarker(id, "bpmn-token-active");
            canvas.addMarker(id, "bpmn-token-visited");
          } catch {
            // Element not on this render of the diagram — skip it.
          }
        }
      }
      for (const id of stepIds(resolved.steps[next])) {
        try {
          canvas.addMarker(id, "bpmn-token-active");
        } catch {
          continue;
        }
        if (!markedRef.current.includes(id)) markedRef.current.push(id);
      }
      return next;
    });
  }, [viewer, resolved, stop]);

  // ── Mode 2: all-simulated-cases replay ───────────────────────────────────
  const bumpBadge = React.useCallback((overlays, elementId, add) => {
    const existing = badgesRef.current.get(elementId);
    if (existing) {
      existing.count += add;
      existing.node.textContent = fmtInt(existing.count);
      return;
    }
    const node = document.createElement("div");
    node.className = "bpmn-case-badge";
    node.textContent = fmtInt(add);
    node.title = "Simulated cases that arrived at this step (of " + fmtInt(totalCases) + ")";
    try {
      const overlayId = overlays.add(elementId, "case-count", {
        position: { top: -12, right: -8 },
        html: node,
      });
      badgesRef.current.set(elementId, { overlayId, node, count: add });
    } catch {
      // Element not on the diagram (unresolved name) — no badge.
    }
  }, [totalCases]);

  const advanceCases = React.useCallback(() => {
    if (!viewer || !waves) return;
    let canvas, overlays;
    try {
      canvas = viewer.get("canvas");
      overlays = viewer.get("overlays");
    } catch {
      stop();
      return;
    }
    const cur = caseCursorRef.current;
    let wave = waves[cur.wave];
    if (!wave) { stop(); return; }

    // Retire the previous step's active highlight.
    const prevStep = cur.step >= 0 ? wave.resolved.steps[cur.step] : null;
    if (prevStep != null) {
      for (const id of stepIds(prevStep)) {
        try {
          canvas.removeMarker(id, "bpmn-token-active");
          canvas.addMarker(id, "bpmn-token-visited");
        } catch { /* skip */ }
      }
    }

    let nextStep = cur.step + 1;
    if (nextStep >= wave.resolved.steps.length) {
      // Wave finished — move to the next one (or finish the replay).
      cur.wave += 1;
      cur.step = -1;
      wave = waves[cur.wave];
      if (!wave) {
        stop();
        setCaseStatus({ done: true });
        return;
      }
      nextStep = 0;
    }
    cur.step = nextStep;

    const step = wave.resolved.steps[nextStep];
    for (const id of stepIds(step)) {
      try {
        canvas.addMarker(id, "bpmn-token-active");
        if (!markedRef.current.includes(id)) markedRef.current.push(id);
      } catch { /* skip */ }
      bumpBadge(overlays, id, wave.cases);
    }
    const firstId = stepIds(step)[0];
    setCaseStatus({
      done: false,
      waveNo: cur.wave + 1,
      waveCount: waves.length,
      routeNo: wave.routeNo,
      cases: wave.cases,
      sharePct: wave.sharePct,
      stepNo: nextStep + 1,
      stepCount: wave.resolved.steps.length,
      label: typeof step === "string" ? (nameById.get(firstId) || firstId) : "Parallel branches",
    });
  }, [viewer, waves, stop, bumpBadge, nameById]);

  const play = React.useCallback(() => {
    if (!viewer) return;
    if (mode === "route") {
      if (!resolved.steps.length) return;
      if (stepIndex >= resolved.steps.length - 1) {
        clearMarkers();
        setStepIndex(-1);
      }
    } else {
      if (!waves) return;
      if (caseStatus?.done) {
        clearMarkers();
        clearBadges();
        caseCursorRef.current = { wave: 0, step: -1 };
        setCaseStatus(null);
      }
    }
    setPlaying(true);
  }, [viewer, mode, resolved, stepIndex, waves, caseStatus, clearMarkers, clearBadges]);

  React.useEffect(() => {
    if (!playing) return undefined;
    const tick = mode === "route" ? advanceRoute : advanceCases;
    tick();
    timerRef.current = setInterval(tick, STEP_MS_BY_SPEED[speed] || STEP_MS_BY_SPEED.normal);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, speed, mode]);

  if (!viewer || !rows.length) return null;

  const currentStep = resolved.steps[stepIndex];
  const routeLabel =
    stepIndex < 0
      ? "Ready"
      : currentStep == null
        ? "Done"
        : typeof currentStep === "string"
          ? nameById.get(currentStep) || currentStep
          : "Parallel branches";
  const routeDone = stepIndex >= 0 && stepIndex >= resolved.steps.length - 1 && !playing;
  const casesStarted = caseStatus != null;
  const casesDone = caseStatus?.done === true;
  const started = mode === "route" ? stepIndex >= 0 : casesStarted;
  const done = mode === "route" ? routeDone : casesDone;

  const statusText =
    mode === "route"
      ? stepIndex < 0
        ? "Ready"
        : `Step ${Math.min(stepIndex + 1, resolved.steps.length)} of ${resolved.steps.length}: ${routeLabel}`
      : !casesStarted
        ? `Ready — ${fmtInt(totalCases)} simulated cases to replay across ${waves ? waves.length : 0} route(s)`
        : casesDone
          ? `Done — all ${fmtInt(totalCases)} cases replayed; each badge = cases that arrived at that step`
          : `Route #${caseStatus.routeNo} (${fmtInt(caseStatus.cases)} cases · ${caseStatus.sharePct}%) — wave ${caseStatus.waveNo} of ${caseStatus.waveCount} · step ${caseStatus.stepNo} of ${caseStatus.stepCount}: ${caseStatus.label}`;

  return (
    <div style={{ marginTop: 14, padding: "12px 16px", border: "1px solid #eef2f7", borderRadius: 10, background: "#fcfcfd" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>
        TOKEN WALKTHROUGH — replay on the diagram above (same enumerated routes as the Path View table below)
      </div>
      {waves && (
        <div role="group" aria-label="Animation mode" style={{ display: "inline-flex", gap: 0, marginBottom: 10, border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
          <button
            type="button"
            aria-pressed={mode === "route"}
            onClick={() => setMode("route")}
            style={{ fontSize: 12, fontWeight: 700, padding: "5px 12px", border: "none", cursor: "pointer", background: mode === "route" ? "#0f766e" : "#fff", color: mode === "route" ? "#fff" : "#334155" }}
          >
            Single route
          </button>
          <button
            type="button"
            aria-pressed={mode === "cases"}
            onClick={() => setMode("cases")}
            style={{ fontSize: 12, fontWeight: 700, padding: "5px 12px", border: "none", borderLeft: "1px solid #cbd5e1", cursor: "pointer", background: mode === "cases" ? "#0f766e" : "#fff", color: mode === "cases" ? "#fff" : "#334155" }}
          >
            All {fmtInt(totalCases)} simulated cases
          </button>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {mode === "route" && (
          <label style={{ fontSize: 12, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
            Route
            <select
              value={pathIndex}
              onChange={(e) => setPathIndex(Number(e.target.value))}
              aria-label="Choose which execution route to animate"
              style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #cbd5e1" }}
            >
              {rows.map((p, i) => (
                <option key={i} value={i}>
                  #{i + 1} · {(p.probability * 100).toFixed(1)}% · {p.duration_minutes} min
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          className="secondary-btn sm"
          onClick={playing ? stop : play}
          disabled={mode === "route" ? !resolved.steps.length : !waves}
          aria-label={playing ? "Pause the animation" : "Play the animation"}
        >
          {playing ? "⏸ Pause" : done ? "▶ Replay" : started ? "▶ Resume" : "▶ Play"}
        </button>
        <button
          type="button"
          className="secondary-btn sm"
          onClick={reset}
          disabled={!started}
          aria-label="Reset the animation to the start"
        >
          ↺ Reset
        </button>
        <label style={{ fontSize: 12, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
          Speed
          <select
            value={speed}
            onChange={(e) => setSpeed(e.target.value)}
            aria-label="Animation speed"
            style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #cbd5e1" }}
          >
            <option value="slow">Slow</option>
            <option value="normal">Normal</option>
            <option value="fast">Fast</option>
          </select>
        </label>
        <span style={{ fontSize: 12, color: "#0e6b68", fontWeight: 700, marginLeft: "auto" }} aria-live="polite">
          {statusText}
        </span>
      </div>
      {mode === "cases" && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#64748b" }}>
          Replays the simulation's case split ({waves ? waves.map((w) => `#${w.routeNo}: ${fmtInt(w.cases)}`).join(" · ") : ""}) route by route,
          largest first. Badges accumulate the cases arriving at each step; a badge ÷ {fmtInt(totalCases)} equals the step's activation
          probability from the per-activity table. Counts cover tasks and sub-process steps (routes are task-level, so gateways/events carry no counter).
        </div>
      )}
      {mode === "route" && resolved.unresolved.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#b45309" }}>
          ⚠ {resolved.unresolved.length} step name{resolved.unresolved.length === 1 ? "" : "s"} in this route didn't match an
          element on the diagram and will be skipped: {resolved.unresolved.slice(0, 3).join(", ")}
          {resolved.unresolved.length > 3 ? "…" : ""}
        </div>
      )}
    </div>
  );
}
